import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore, useSessionReset } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { useInterval } from '@renderer/utils/useInterval'
import MidtransUtils from '@renderer/utils/midtransUtils'
import Spinner from '@renderer/components/Spinner'
import cogoToast from '@successtar/cogo-toast'
import { TransactionStatus } from '@renderer/types/sessionState'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import { formatAmount, normalizeCurrency } from '@renderer/utils/formatCurrency'

const Payment: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { config } = useConfigStore()
  const projectCurrency = normalizeCurrency(config?.resolvedCurrency)
  const { transaction, setSessionState, uploadSessionState, logActivity, id } =
    useSessionStateStore()
  const sessionReset = useSessionReset()
  const isMounted = useRef(true)

  // Helper to safely update state/logs only when component is mounted
  const safeUpdate = (callback: () => void): void => {
    if (isMounted.current) {
      callback()
    }
  }

  const [qrUrl, setQrUrl] = useState<string | null>(null)
  const [isQrLoaded, setIsQrLoaded] = useState(false)
  const [isExpired, setIsExpired] = useState(false)
  const [isCheckingPayment, setIsCheckingPayment] = useState(false)
  const [remainingTime, setRemainingTime] = useState<string>('--:--')
  const [showSuccess, setShowSuccess] = useState(false)
  const [shakeAnimation, setShakeAnimation] = useState(false)

  const paymentConfig = config?.pages?.payment
  const paymentTimeout = paymentConfig?.settings?.paymentTimeout || 120

  // Initialize payment countdown
  const paymentCountdown = useCountdown(0)
  // Initialize auto-reset countdown (15 seconds)
  const autoResetCountdown = useCountdown(15)

  const calculateRemainingTime = (expiryTime: string, serverTime: string): number => {
    try {
      const expiryTimestamp = new Date(expiryTime).getTime()
      const serverTimestamp = new Date(serverTime).getTime()
      const remainingMs = expiryTimestamp - serverTimestamp
      return Math.max(0, Math.floor(remainingMs / 1000))
    } catch (error) {
      console.error('Error calculating remaining time:', error)
      return 0
    }
  }

  const handleCountdownEnd = async (): Promise<void> => {
    if (!isMounted.current) {
      return
    }
    if (id !== useSessionStateStore.getState().id) {
      logActivity('Ignoring timeout for a previous session.')
      return
    }
    await checkPaymentStatus()
    safeUpdate(() => {
      setIsCheckingPayment(false)
      setIsExpired(true)
      logActivity('Payment timed out.', {
        orderId: transaction.orderId
      })
      setSessionState({
        transaction: {
          status: 'expire'
        }
      })
    })
  }

  // Set up payment countdown callbacks
  useEffect(() => {
    paymentCountdown.setOnTick(() => {
      setRemainingTime(paymentCountdown.getFormattedRemainingSeconds())
    })
    paymentCountdown.setOnComplete(handleCountdownEnd)
  }, [])

  // Set up auto-reset countdown callbacks
  useEffect(() => {
    autoResetCountdown.setOnTick(() => {
      setRemainingTime(autoResetCountdown.getFormattedRemainingSeconds())
    })
    autoResetCountdown.setOnComplete(() => {
      sessionReset()
    })
  }, [])

  // Watch for payment expiration to start auto-reset countdown
  useEffect(() => {
    if (isExpired) {
      autoResetCountdown.start()
      return (): void => {
        autoResetCountdown.stop()
      }
    }
    return
  }, [isExpired])

  const cleanupPayment = useCallback(async (): Promise<void> => {
    if (transaction.orderId && transaction.status !== 'settlement') {
      try {
        await fetch(`${process.env.VITE_API_URL}/api/payments/${transaction.orderId}`, {
          method: 'DELETE',
          credentials: 'include'
        })
      } catch (error) {
        console.error('Error cancelling payment:', error)
      }
    }
  }, [transaction.orderId, transaction.status])

  const initiatePayment = async (): Promise<void> => {
    try {
      const data = await MidtransUtils.initiatePayment(
        transaction.amount || 0,
        id || '',
        paymentTimeout,
        projectId || '',
        '',
        '',
        transaction.discount || 0
      )

      if (data.order_id && data.qr_url) {
        const initialRemainingSeconds = calculateRemainingTime(data.expiry_time, data.server_time)

        if (initialRemainingSeconds <= 0) {
          setIsExpired(true)
          return
        }

        const sanitizedQrUrl = data.qr_url.replace(/([^:]\/)\/+/g, '$1')
        setQrUrl(sanitizedQrUrl)
        setIsQrLoaded(false)

        setSessionState({
          transaction: {
            orderId: data.order_id,
            qrUrl: sanitizedQrUrl,
            expiryTime: data.expiry_time,
            serverTime: data.server_time,
            status: 'pending' as TransactionStatus
          }
        })

        setIsExpired(false)
        paymentCountdown.stop()
        paymentCountdown.setRemainingSeconds(initialRemainingSeconds)
        paymentCountdown.start()
        setIsCheckingPayment(true)
        isMounted.current = true

        logActivity('Payment initiated.', {
          amount: transaction.amount,
          orderId: data.order_id,
          expiryTime: data.expiry_time,
          serverTime: data.server_time,
          remainingSeconds: initialRemainingSeconds
        })
      }
    } catch (e: unknown) {
      logActivity(`Payment initiation failed: ${(e as Error).message}`, {
        error: (e as Error).message,
        level: 'error'
      })
      console.error('Error initiating payment:', e)
      cogoToast.error((e as Error).message)
    }
  }

  const checkPaymentStatus = async (): Promise<void> => {
    if (!transaction.expiryTime || !transaction.serverTime || !isMounted.current) {
      return
    }

    try {
      const paymentData = await MidtransUtils.checkPaymentStatus(
        transaction.orderId || 'order-default-id'
      )

      if (paymentData.isExpired) {
        safeUpdate(() => {
          setIsCheckingPayment(false)
          setIsExpired(true)
          logActivity('Payment expired.', {
            orderId: transaction.orderId,
            expiryTime: transaction.expiryTime,
            serverTime: paymentData.serverTime,
            transactionStatus: paymentData.transactionStatus
          })
          setSessionState({
            transaction: {
              status: 'expire'
            }
          })
          uploadSessionState()
        })
        return
      }

      if (paymentData.paid) {
        safeUpdate(() => {
          setIsCheckingPayment(false)
          cogoToast.success('Payment successful!')
          logActivity('Payment successful.', {
            orderId: transaction.orderId,
            serverTime: paymentData.serverTime,
            transactionStatus: paymentData.transactionStatus
          })
          setSessionState({
            transaction: {
              status: 'settlement'
            }
          })
          uploadSessionState()

          // Show success animation
          setShowSuccess(true)

          // Start shake animation after a brief delay
          setTimeout(() => {
            setShakeAnimation(true)
          }, 100)

          // Navigate to next page after animation
          setTimeout(() => {
            navigate(`/${projectId}/capture`)
          }, 2000) // 2 seconds total for animation
        })
      }
    } catch (e: unknown) {
      console.error('Error checking payment status:', e)
    }
  }

  useEffect(() => {
    initiatePayment()
    uploadSessionState()

    return (): void => {
      isMounted.current = false
      setIsCheckingPayment(false)
      paymentCountdown.stop()
      autoResetCountdown.stop()
      cleanupPayment()
    }
  }, [])

  useInterval(
    async () => {
      await checkPaymentStatus()
    },
    isCheckingPayment ? 5000 : null
  )

  const handleBack = (): void => {
    logActivity('User clicked back button on payment page')
    navigate(`/${projectId}/checkout`)
  }

  const cachedPaymentBg = useCachedBackgroundImage('/assets/boothndaughter/payment.webp')
  const cachedPaymentSuccessBg = useCachedBackgroundImage(
    '/assets/boothndaughter/payment-success.webp'
  )

  return (
    <div
      className="w-full h-full relative overflow-hidden"
      style={{
        backgroundImage: `url(${cachedPaymentBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Payment Success Animation */}
      {showSuccess && (
        <div className="absolute right-48 top-1/2 transform -translate-y-1/2">
          <div
            className={`transition-all duration-300 ${shakeAnimation ? 'animate-bounce' : ''}`}
            style={{
              width: '544px',
              height: '319px',
              backgroundImage: `url(${cachedPaymentSuccessBg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          />
        </div>
      )}

      {/* Back Button */}
      <div className="absolute bottom-12 left-24">
        <button
          onClick={handleBack}
          style={{
            fontFamily: 'monospace',
            fontSize: '32px',
            color: 'white',
            textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
            background: 'none',
            border: 'none',
            cursor: 'pointer'
          }}
          className="hover:opacity-80 transition-opacity active:scale-95 transform"
        >
          ← Back
        </button>
      </div>

      {/* Price Display */}
      <div className="absolute bottom-[145px] left-1/2 transform -translate-x-1/2 -translate-y-1/2">
        <div className="text-center">
          {qrUrl && isQrLoaded && (
            <span
              style={{
                fontFamily: 'monospace',
                fontSize: '48px',
                color: 'blue',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                fontWeight: 'bold'
              }}
            >
              {formatAmount(transaction.amount || 0, projectCurrency)}
            </span>
          )}
        </div>
      </div>

      {/* QR Code Box */}
      <div className="flex items-center justify-center mt-[380px]">
        <div className="bg-white border-4 border-gray-300 rounded-lg px-4 pb-12 shadow-2xl max-w-lg w-full">
          {/* QR Code */}
          <div className="flex justify-center mb-4">
            <div className="w-320 h-320 flex items-center justify-center">
              {qrUrl && (
                <img
                  src={qrUrl}
                  alt="QR Code"
                  className={`w-320 h-320 object-contain ${isQrLoaded ? 'block' : 'hidden'}`}
                  crossOrigin="anonymous"
                  onLoad={() => setIsQrLoaded(true)}
                />
              )}
              {(!qrUrl || !isQrLoaded) && (
                <div className="w-96 h-96 flex items-center justify-center">
                  <Spinner />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Timeout Circle */}
      <div className="absolute bottom-8 right-8">
        <div className="w-36 h-36 border-4 border-redYotta rounded-full flex items-center justify-center">
          <span
            style={{
              fontFamily: 'monospace',
              fontSize: '36px',
              color: '#EF4444',
              fontWeight: 'bold'
            }}
          >
            {remainingTime}
          </span>
        </div>
      </div>

      {/* Expired State */}
      {isExpired && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-12 rounded-lg text-center shadow-2xl">
            <h2
              style={{
                fontFamily: 'monospace',
                fontSize: '32px',
                fontWeight: 'bold',
                color: '#374151',
                marginBottom: '16px'
              }}
            >
              Payment Expired
            </h2>
            <p
              style={{
                fontFamily: 'monospace',
                fontSize: '18px',
                color: '#6B7280',
                marginBottom: '24px'
              }}
            >
              The payment code has expired. You will be redirected automatically.
            </p>
            <p
              style={{
                fontFamily: 'monospace',
                fontSize: '14px',
                color: '#9CA3AF'
              }}
            >
              Auto-restart in {remainingTime}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default Payment
